/**
 * Session-scoped record of the last authorization answer the hosted Worker gave
 * us. The Worker is the only authority on owner access, so the client keeps no
 * user id of its own and simply remembers whether the most recent authenticated
 * request came back as `owner_only`.
 *
 * The record is scoped to the Clerk account it was observed for. A denial that
 * outlived a sign-out would lock the browser out of the app for the next
 * account too, so the gate would keep showing "private alpha" to a valid owner
 * until a full reload.
 */
import { create } from "zustand";

import type { HostedApiErrorCode } from "./api";

interface HostedAccessStore {
  /** Clerk user id the current record belongs to, or null when signed out. */
  readonly accountId: string | null;
  readonly errorCode: HostedApiErrorCode | null;
  readonly errorMessage: string | null;
  readonly recordAccessResult: (input: {
    readonly code: HostedApiErrorCode | null;
    readonly message?: string | null;
  }) => void;
  /**
   * Point the record at the signed-in account. Any change (sign-in, sign-out,
   * account switch) drops the previous account's answer, because it says
   * nothing about this one.
   */
  readonly syncAccount: (accountId: string | null) => void;
  readonly clear: () => void;
}

/** Only authorization outcomes change the gate; transport failures must not lock the user out. */
const GATE_RELEVANT_CODES: ReadonlySet<HostedApiErrorCode> = new Set([
  "owner_only",
  "unauthenticated",
]);

export const useHostedAccessStore = create<HostedAccessStore>((set) => ({
  accountId: null,
  errorCode: null,
  errorMessage: null,
  recordAccessResult: ({ code, message }) =>
    set((state) => {
      if (code === null) {
        return state.errorCode === null ? state : { errorCode: null, errorMessage: null };
      }
      if (!GATE_RELEVANT_CODES.has(code)) {
        return state;
      }
      return { errorCode: code, errorMessage: message?.trim() || null };
    }),
  syncAccount: (accountId) =>
    set((state) =>
      state.accountId === accountId ? state : { accountId, errorCode: null, errorMessage: null },
    ),
  clear: () => set({ errorCode: null, errorMessage: null }),
}));

export function recordHostedAccessResult(input: {
  readonly code: HostedApiErrorCode | null;
  readonly message?: string | null;
}): void {
  useHostedAccessStore.getState().recordAccessResult(input);
}

export function syncHostedAccessAccount(accountId: string | null): void {
  useHostedAccessStore.getState().syncAccount(accountId);
}

export interface HostedAccessRecord {
  readonly errorCode: HostedApiErrorCode | null;
  readonly errorMessage: string | null;
}

const NO_ACCESS_RECORD: HostedAccessRecord = { errorCode: null, errorMessage: null };

/**
 * The Worker's answer for `accountId`, or an empty record when the stored one
 * belongs to a different account. Reading through this rather than the raw
 * fields means a sign-in cannot render one frame of the previous account's
 * denial while the sync effect is still pending.
 */
export function selectHostedAccessRecord(
  state: { readonly accountId: string | null } & HostedAccessRecord,
  accountId: string | null,
): HostedAccessRecord {
  if (state.accountId !== accountId) {
    return NO_ACCESS_RECORD;
  }
  return state.errorCode === null
    ? NO_ACCESS_RECORD
    : { errorCode: state.errorCode, errorMessage: state.errorMessage };
}
